import React from 'react';

/**
 * CategoriesMultiselect
 * ------------------------------------------------------------------
 * Grid of toggleable chips for "select all that apply" style fields
 * (e.g. Music, Film, Visual Arts...). Fully controlled: the parent
 * owns the list of selected values.
 *
 * @param {string} label - field label, e.g. "Categories"
 * @param {string} helperText - small hint next to the label, e.g. "Select all that apply"
 * @param {{value: string, label: string}[]} options
 * @param {string[]} selected - array of currently selected option values
 * @param {function} onChange - (nextSelectedArray) => void
 * @param {string} checkIconSrc - checkmark icon shown inside a selected chip
 */
const CategoriesMultiselect = ({
  label = 'Categories',
  helperText,
  options = [],
  selected = [],
  onChange,
  checkIconSrc,
  name = 'amok-categories',
  error,
  required = false,
  className = '',
}) => {
  const isSelectedValue = (value) =>
    selected.some((item) => String(item) === String(value));

  const toggle = (value) => {
    if (!onChange) return;
    const next = isSelectedValue(value)
      ? selected.filter((item) => String(item) !== String(value))
      : [...selected, value];
    onChange(next);
  };

  return (
    <fieldset className={`amok-categories ${className}`.trim()}>
      <legend className="amok-categories__legend">
        <span className="amok-categories__label">
          {label}
          {required && <span className="amok-field__required"> *</span>}
        </span>
        {helperText && (
          <span className="amok-categories__helper">{helperText}</span>
        )}
      </legend>

      <div className="amok-categories__grid" role="group" aria-label={label}>
        {options.map((option) => {
          const isChecked = isSelectedValue(option.value);
          return (
            <button
              key={option.value}
              type="button"
              role="checkbox"
              aria-checked={isChecked}
              name={name}
              value={option.value}
              className={`amok-category-chip ${
                isChecked ? 'amok-category-chip--selected' : ''
              }`}
              onClick={() => toggle(option.value)}
            >
              {isChecked && checkIconSrc ? (
                <img
                  src={checkIconSrc}
                  alt=""
                  className="amok-category-chip__check"
                />
              ) : (
                <span className="amok-category-chip__box" aria-hidden="true" />
              )}
              <span className="amok-category-chip__label">{option.label}</span>
            </button>
          );
        })}
      </div>
      {error && (
        <p className="amok-field__message amok-field__message--error">
          {error}
        </p>
      )}
    </fieldset>
  );
};

export default CategoriesMultiselect;
